/*
 * Host-aware FusionMSI document branding.
 *
 * index.html ships the FusionMSI favicon by default. This helper still applies
 * (and restores) favicon / apple-touch-icon / og+twitter social-image meta while
 * a FusionMSI surface is mounted — useful when another brand manager (e.g.
 * VoxPont) has swapped the head tags, and for og:image which is not in index.html.
 *
 * Mirrors src/pages/voxpontmarketing/voxpontBranding.js. Assets are generated by
 * scripts/build-fusionmsi-assets.mjs into public/brand/fusionmsi.
 *
 * Note: crawlers that don't execute JS won't see runtime og/twitter tags; the
 * fusionmsi.com host build (scripts/build-fusionmsi-host.mjs) bakes those into
 * its index.html for full crawler coverage.
 */

const BRAND_BASE = "/brand/fusionmsi";

const FAVICON_SVG = `${BRAND_BASE}/favicon.svg`;
const FAVICON_PNG = `${BRAND_BASE}/favicon-64.png`;
const APPLE_ICON = `${BRAND_BASE}/app-icon-180.png`;
const SOCIAL_IMAGE = `${BRAND_BASE}/og-image.png`;

/** Find or create a <link rel> in <head>; returns the element + created flag. */
function ensureLink(selector, create) {
  let el = document.head.querySelector(selector);
  let created = false;
  if (!el) {
    el = create();
    document.head.appendChild(el);
    created = true;
  }
  return { el, created };
}

/** Find or create a <meta> by name/property; returns the element + created flag. */
function ensureMeta(attr, value) {
  const selector = `meta[${attr}="${value}"]`;
  let el = document.head.querySelector(selector);
  let created = false;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, value);
    document.head.appendChild(el);
    created = true;
  }
  return { el, created };
}

/**
 * Apply FusionMSI favicon + social meta. Returns a cleanup function that restores
 * the prior document head state.
 */
export function applyFusionMsiBranding() {
  const restores = [];

  // --- Favicon (rel="icon"). Prefer the crisp SVG, with a PNG fallback link. ---
  const icon = ensureLink('link[rel="icon"]', () => {
    const l = document.createElement("link");
    l.setAttribute("rel", "icon");
    return l;
  });
  const prevIconHref = icon.el.getAttribute("href");
  const prevIconType = icon.el.getAttribute("type");
  icon.el.setAttribute("type", "image/svg+xml");
  icon.el.setAttribute("href", FAVICON_SVG);
  restores.push(() => {
    if (icon.created) {
      icon.el.remove();
    } else {
      if (prevIconHref != null) icon.el.setAttribute("href", prevIconHref);
      if (prevIconType != null) icon.el.setAttribute("type", prevIconType);
      else icon.el.removeAttribute("type");
    }
  });

  // PNG fallback for engines without SVG favicon support.
  const iconPng = ensureLink('link[rel="alternate icon"]', () => {
    const l = document.createElement("link");
    l.setAttribute("rel", "alternate icon");
    l.setAttribute("type", "image/png");
    return l;
  });
  const prevIconPngHref = iconPng.el.getAttribute("href");
  iconPng.el.setAttribute("href", FAVICON_PNG);
  restores.push(() => {
    if (iconPng.created) iconPng.el.remove();
    else if (prevIconPngHref != null) iconPng.el.setAttribute("href", prevIconPngHref);
  });

  // --- Apple touch icon ---
  const apple = ensureLink('link[rel="apple-touch-icon"]', () => {
    const l = document.createElement("link");
    l.setAttribute("rel", "apple-touch-icon");
    return l;
  });
  const prevAppleHref = apple.el.getAttribute("href");
  apple.el.setAttribute("href", APPLE_ICON);
  restores.push(() => {
    if (apple.created) apple.el.remove();
    else if (prevAppleHref != null) apple.el.setAttribute("href", prevAppleHref);
  });

  // --- Social meta (og + twitter) ---
  const socialUrl = `${window.location.origin}${SOCIAL_IMAGE}`;
  const metaPairs = [
    ["property", "og:image"],
    ["name", "twitter:image"],
    ["name", "twitter:card"],
  ];
  const metaValues = {
    "og:image": socialUrl,
    "twitter:image": socialUrl,
    "twitter:card": "summary_large_image",
  };
  for (const [attr, key] of metaPairs) {
    const { el, created } = ensureMeta(attr, key);
    const prevContent = el.getAttribute("content");
    el.setAttribute("content", metaValues[key]);
    restores.push(() => {
      if (created) el.remove();
      else if (prevContent != null) el.setAttribute("content", prevContent);
      else el.removeAttribute("content");
    });
  }

  return () => {
    // Restore in reverse so created-element removals are clean.
    for (let i = restores.length - 1; i >= 0; i -= 1) restores[i]();
  };
}
